import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import { getAccessToken, clearAuthData, removeAccessToken, removeUserId } from 'utils/sessionStorage';
import { shouldSuppressCamSessionInterrupt } from 'utils/camAuth';
import { reportFailedWrite, reportMissingData, reportUnexpected, shouldSkipHttpSentryReport } from '../sentry';

const http = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',

  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET,OPTIONS,PATCH,DELETE,POST,PUT',
    'Access-Control-Allow-Headers':
      'X-CSRF-Token, X-Requested-With, Accept, Accept-Version, Content-Length, Content-MD5, Content-Type, Date, X-Api-Version, Authorization',
  },
});

http.interceptors.request.use(
  function (config: AxiosRequestConfig) {
    const auToken = getAccessToken();
    if (!config) {
      config = {};
    }
    if (!config.headers) {
      config.headers = {};
    }
    // Only attach the header when a token exists — avoids sending "Bearer undefined"
    // (which the backend answers with a 401) from public pages such as /public/login.
    if (auToken) {
      config.headers.Authorization = 'Bearer ' + auToken;
    } else {
      delete config.headers.Authorization;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Guards against N parallel 401s each assigning window.location.href.
// Reset once the user actually lands on a public route, so a later 401
// (after re-login in the same tab, HashRouter = no page reload) still redirects.
let isRedirectingToLogin = false;
if (typeof window !== 'undefined') {
  window.addEventListener('hashchange', () => {
    if ((window.location.hash || '').startsWith('#/public/')) {
      isRedirectingToLogin = false;
    }
  });
}

const handleRevokedSession = () => {
  if (shouldSuppressCamSessionInterrupt()) {
    return;
  }
  clearAuthData();
  removeAccessToken();
  removeUserId();
  Cookies.remove('refresh_token');
  window.location.href = '/#/public/login';
};

const FRIENDLY_DEFAULT = "That didn't go through — please try again in a moment.";

function friendlyByStatus(status?: number): string {
  if (!status) {
    return "You appear to be offline. Your changes are safe — we'll finish up once you're reconnected.";
  }
  if (status >= 500) {
    return 'Something on our end hiccuped. Our team has been notified — please try again shortly.';
  }
  if (status === 429) {
    return "That's a lot at once — give it a second and try again.";
  }
  if (status === 413) {
    return 'That entry is a little too large to save.';
  }
  if (status === 404) {
    return "We couldn't find that — it may have been updated. Try refreshing the page.";
  }
  if (status === 403) {
    return "You don't have access to that right now.";
  }
  return FRIENDLY_DEFAULT;
}

function looksTechnical(text: string): boolean {
  if (!text) return true;
  if (text.length > 140) return true;
  return /invalid|error|exception|failed|null|undefined|constraint|ECONN|stack|\bDto\b|query/i.test(
    text
  );
}

function userFacingClientMessage(rawMessage: unknown, status?: number): string {
  const raw = Array.isArray(rawMessage)
    ? rawMessage.join(', ')
    : rawMessage
    ? String(rawMessage)
    : '';
  return looksTechnical(raw) ? friendlyByStatus(status) : raw;
}

function isGetRequest(error: any): boolean {
  return String(error?.config?.method || 'get').toLowerCase() === 'get';
}

function isWriteRequest(method?: string): boolean {
  return /^(post|put|patch|delete)$/i.test(method || '');
}

function reportWriteFailure(
  method: string | undefined,
  url: string | undefined,
  status: number | undefined,
  responseMessage: unknown
): void {
  if (!isWriteRequest(method)) return;
  if (status === 401 || status === 430) return;
  if (shouldSkipHttpSentryReport(url, status)) return;
  reportFailedWrite({ url, method, status, responseMessage });
}

http.interceptors.response.use(
  function (response: AxiosResponse) {
    let returnData: any = response.data;
    const statusCode = response.status;

    if (statusCode === 430 && returnData?.code === 'SESSION_EXPIRED') {
      clearAuthData();
      removeAccessToken();
      removeUserId();
      Cookies.remove('refresh_token');
      window.location.href = '/#/public/session-conflict';
      return Promise.reject(new Error('SESSION_EXPIRED'));
    }

    if (returnData?.errorCode === 430) {
      handleRevokedSession();
      return Promise.reject(new Error(returnData?.message || 'Session revoked'));
    }

    // Null/empty GET bodies (e.g. college-university with no row) must not throw.
    if (
      returnData &&
      typeof returnData === 'object' &&
      typeof returnData.status === 'number' &&
      returnData.status >= 400
    ) {
      reportWriteFailure(
        response.config?.method,
        response.config?.url,
        returnData.status,
        returnData?.message
      );
      toast(userFacingClientMessage(returnData?.message, returnData.status));
      return Promise.reject();
    }

    return response.data;
  },
  (error) => {
    try {
      if (error.response) {
        const responseData = error.response.data || {};
        const statusCode = error.response.status;

        if (statusCode === 430 && responseData.code === 'SESSION_EXPIRED') {
          if (window.location.hash !== '#/public/session-conflict') {
            clearAuthData();
            removeAccessToken();
            removeUserId();
            Cookies.remove('refresh_token');
            window.location.href = '/#/public/session-conflict';
          }
          return Promise.reject(error);
        }

        if (responseData.errorCode === 430) {
          if (window.location.hash !== '#/public/login') {
            handleRevokedSession();
          }
          return Promise.reject(error);
        }

        if (statusCode === 430 && responseData?.code === 'SESSION_EXISTS') {
          return Promise.reject(error);
        }

        // 401 = access token expired/invalid. Expected auth flow, not a product bug:
        // clear the session, send the user to login once (no redirect loop when
        // already there), and flag the error so Sentry drops the unhandled rejection.
        if (statusCode === 401) {
          error.isAuthExpired = true;
          const onPublicRoute = (window.location.hash || '').startsWith('#/public/');
          if (!shouldSuppressCamSessionInterrupt()) {
            clearAuthData();
            Cookies.remove('refresh_token');
            if (!onPublicRoute && !isRedirectingToLogin) {
              isRedirectingToLogin = true;
              window.location.href = '/#/public/login';
            }
          }
          return Promise.reject(error);
        }

        // Stringify so the console/Sentry breadcrumb shows the real payload instead of "[object Object]".
        console.error(
          'Server responded with an error:',
          statusCode,
          error.config?.url,
          JSON.stringify(responseData)
        );

        // Missing resource (404): still report so we see systemic data gaps
        // (many users → one Sentry issue). GET 404 stays silent in the UI.
        if (statusCode === 404) {
          reportMissingData({
            url: error.config?.url,
            method: error.config?.method,
            status: statusCode,
            responseMessage: responseData?.message,
          });
          if (isGetRequest(error)) {
            return Promise.reject(error);
          }
          reportWriteFailure(
            error.config?.method,
            error.config?.url,
            statusCode,
            responseData?.message
          );
          toast(userFacingClientMessage(responseData?.message, statusCode), {
            style: { padding: 10 },
          });
          return Promise.reject(error);
        }

        if (!statusCode || statusCode >= 500) {
          reportUnexpected(error, {
            url: error.config?.url,
            method: error.config?.method,
            status: statusCode,
            responseMessage: responseData?.message,
          });
          toast.error(friendlyByStatus(statusCode), { style: { padding: 10 } });
        } else {
          reportWriteFailure(
            error.config?.method,
            error.config?.url,
            statusCode,
            responseData?.message
          );
          toast(userFacingClientMessage(responseData?.message, statusCode), {
            style: { padding: 10 },
          });
        }
      } else if (error.request) {
        console.error('No response received from server:', error.request);
        toast(friendlyByStatus(undefined));
      } else {
        console.error('Error setting up request:', error.message);
        reportUnexpected(error);
      }
    } catch (ex) {
      console.log('Error: ', ex);
      reportUnexpected(ex);
    }
    return Promise.reject(error);
  }
);

export default http;
